import React, {createContext, useCallback, useEffect, useRef, useState} from "react";
import apiFetch from "./api";
import { DEFAULT_VENUE_TYPE } from "../lib/consts/venueTypes";
import { isCognitoEnabled, cognitoRefreshSession } from "../lib/cognito";
import {
  cerrarSesion,
  guardarSesion,
  leerEmail,
  leerRefreshToken,
  leerToken,
  suscribirSincronizacionPestanas,
} from "../lib/authSession";

export const UserContext = createContext();

export const UserProvider = (props) => {
    const [token, setToken] = useState(leerToken());
    const cognitoRefreshTimer = useRef(null);
    const [ownedShops, setOwnedShops] = useState([]);
    const [allVenues, setAllVenues] = useState([]); // All venues for superusers
    const [email, setEmail] = useState("");
    const [accessPrivileges, setAccessPrivileges] = useState("");
    const [canManageProducts, setCanManageProducts] = useState(false);
    const [venueID, setVenueID] = useState(localStorage.getItem("selectedVenueID") || "");
    const [venueName, setVenueName] = useState(localStorage.getItem("selectedVenueName") || "");
    const [venueImage, setVenueImage] = useState(localStorage.getItem("selectedVenueImage") || "");
    const [venueType, setVenueType] = useState(localStorage.getItem("selectedVenueType") || DEFAULT_VENUE_TYPE);
    const [venueActivationStatus, setVenueActivationStatus] = useState(localStorage.getItem("selectedVenueActivationStatus") || "pending");
    const [venueSubscriptionStatus, setVenueSubscriptionStatus] = useState(localStorage.getItem("selectedVenueSubscriptionStatus") || "");
    // SCRUM-54: el guard distingue "prueba vencida" de "nunca activada" con esta fecha.
    const [venueTrialEndsAt, setVenueTrialEndsAt] = useState(localStorage.getItem("selectedVenueTrialEndsAt") || "");
    // Comandero (CRUO): con esto el menú decide si pinta "Barra" (issue #456).
    const [venueOrdersEnabled, setVenueOrdersEnabled] = useState(localStorage.getItem("selectedVenueOrdersEnabled") === "true");
    // Smack (chat equipo+club): con esto el menú decide si pinta "Smack".
    const [venueSmackEnabled, setVenueSmackEnabled] = useState(localStorage.getItem("selectedVenueSmackEnabled") === "true");
    // true cuando /v1/users/me ya resolvió (éxito o error) para el token actual.
    // Único punto de verdad de "sesión resuelta": antes PrivateRoutes/Login tenían
    // su propio fetch duplicado (useUserTokenPrivileges), sin caché ni cancelación,
    // que se repetía en cada montaje y podía dejar al router rebotando entre
    // /login y /dashboard si el backend tardaba (issue: "se queda atrapada entre
    // login y dashboard").
    // Va ligada AL TOKEN, no es un booleano suelto: tras logout+login en la misma
    // pestaña, el primer render con el token nuevo ya sale «sin resolver» (con un
    // booleano seguía en true del estado sin token, y PrivateRoutes mandaba al login
    // antes de que el efecto lo bajara).
    const [sesionResueltaPara, setSesionResueltaPara] = useState(null);
    const userReady = !token || sesionResueltaPara === token;

    const persistSelectedVenue = useCallback((venue) => {
      const nextVenueID = String(venue?.id || "");
      const nextVenueName = venue?.name || "";
      const nextVenueImage = venue?.image_path || "";
      const nextVenueType = venue?.venue_type || DEFAULT_VENUE_TYPE;
      const nextVenueActivationStatus = venue?.activation_status || "pending";
      const nextVenueSubscriptionStatus = venue?.subscription_status || "";
      const nextVenueTrialEndsAt = venue?.trial_ends_at || "";
      const nextVenueOrdersEnabled = Boolean(venue?.orders_enabled);
      const nextVenueSmackEnabled = Boolean(venue?.smack_enabled);

      setVenueID(nextVenueID);
      setVenueName(nextVenueName);
      setVenueImage(nextVenueImage);
      setVenueType(nextVenueType);
      setVenueActivationStatus(nextVenueActivationStatus);
      setVenueSubscriptionStatus(nextVenueSubscriptionStatus);
      setVenueTrialEndsAt(nextVenueTrialEndsAt);
      setVenueOrdersEnabled(nextVenueOrdersEnabled);
      setVenueSmackEnabled(nextVenueSmackEnabled);

      localStorage.setItem("selectedVenueID", nextVenueID);
      localStorage.setItem("selectedVenueName", nextVenueName);
      localStorage.setItem("selectedVenueImage", nextVenueImage);
      localStorage.setItem("selectedVenueType", nextVenueType);
      localStorage.setItem("selectedVenueActivationStatus", nextVenueActivationStatus);
      localStorage.setItem("selectedVenueSubscriptionStatus", nextVenueSubscriptionStatus);
      localStorage.setItem("selectedVenueTrialEndsAt", nextVenueTrialEndsAt);
      localStorage.setItem("selectedVenueOrdersEnabled", String(nextVenueOrdersEnabled));
      localStorage.setItem("selectedVenueSmackEnabled", String(nextVenueSmackEnabled));
    }, []);

    /** El comandero se activa desde Configuración: estado y caché, a la vez. */
    const aplicarOrdersEnabled = useCallback((valor) => {
      const activo = Boolean(valor);
      setVenueOrdersEnabled(activo);
      localStorage.setItem("selectedVenueOrdersEnabled", String(activo));
    }, []);

    /** Smack se activa desde Configuración: estado y caché, a la vez (mismo motivo
     *  que aplicarOrdersEnabled: sin esto el menú espera a la segunda recarga). */
    const aplicarSmackEnabled = useCallback((valor) => {
      const activo = Boolean(valor);
      setVenueSmackEnabled(activo);
      localStorage.setItem("selectedVenueSmackEnabled", String(activo));
    }, []);

    // Sincronización entre pestañas abiertas: si se abre una pestaña o ventana nueva
    // mientras otra tiene sesión activa, hereda la sesión sin pedir login; si se
    // cierra sesión en una pestaña, se cierra en todas. Si se cierran TODAS las
    // pestañas/navegador, sessionStorage muere y no queda quién responda.
    useEffect(() => {
      return suscribirSincronizacionPestanas({
        onSesionRecibida: (nuevoToken) => {
          setToken((actual) => (actual === nuevoToken ? actual : nuevoToken));
        },
        onSesionCerrada: () => {
          setToken(null);
        },
      });
    }, []);

    // Cognito token refresh — silently renews the ID token every 45 min.
    // Does nothing when AUTH_PROVIDER != cognito or when there is no token.
    useEffect(() => {
      if (!isCognitoEnabled || !token) return;

      const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

      const refresh = async () => {
        const refreshToken = leerRefreshToken();
        const email = leerEmail();
        if (!refreshToken || !email) return;

        try {
          const { idToken, refreshToken: newRefresh } = await cognitoRefreshSession(email, refreshToken);
          guardarSesion({ token: idToken, refreshToken: newRefresh });
          setToken(idToken);
          console.log("[UserContext] Cognito session refreshed silently.");
        } catch (err) {
          console.warn("[UserContext] Cognito refresh failed — forcing re-login:", err.message);
          cerrarSesion();
          setToken(null);
        }
      };

      // Clear any existing timer before setting a new one
      if (cognitoRefreshTimer.current) clearInterval(cognitoRefreshTimer.current);
      cognitoRefreshTimer.current = setInterval(refresh, REFRESH_INTERVAL_MS);

      return () => {
        if (cognitoRefreshTimer.current) clearInterval(cognitoRefreshTimer.current);
      };
    }, [token]);

    useEffect(() => {
        if (!token) {
          // Sin token no hay cuenta: se vacía lo que describía a la anterior. Si se
          // quedara, el siguiente login (otra persona en la misma tablet) arrancaría
          // con el rol y las tiendas de quien salió hasta que llegase su /v1/users/me.
          setAccessPrivileges("");
          setEmail("");
          setOwnedShops([]);
          setAllVenues([]);
          setCanManageProducts(false);
          return;
        }
        // Una respuesta tardía de un token ANTERIOR no debe pisar la del actual.
        let cancelado = false;
        let reintento = null;
        // Red caída o backend frío (Dokploy tarda en despertar; un despliegue de ECS son
        // 1-2 min de 502): antes cada montaje de PrivateRoutes volvía a pedir /v1/users/me
        // y la sesión «se curaba sola» en la siguiente navegación. Ahora solo pide este
        // efecto, así que reintenta él, sin rendirse y con espera creciente (5 → 10 → 20 →
        // 30 s), con la sesión aún «sin resolver» (Cargando…). El token sobrevive: cerrar
        // la sesión por un corte de wifi o un despliegue deslogueaba las tablets del club.
        const ESPERAS_MS = [5000, 10000, 20000, 30000];
        let ronda = 0;

        const fetchWithRetry = async (url, options, retries = 3) => {
          for (let i = 0; i < retries; i++) {
            try {
              const res = await apiFetch(url, options);
              if (res.status === 502 || res.status === 503 || res.status === 504) {
                if (i < retries - 1) {
                  await new Promise(r => setTimeout(r, 1000 * (i + 1)));
                  continue;
                }
              }
              return res;
            } catch (err) {
              if (i < retries - 1) {
                await new Promise(r => setTimeout(r, 1000 * (i + 1)));
                continue;
              }
              throw err;
            }
          }
        };

        const fetchUser = async () => {
          const requestOptions = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          };
      
          try {
            const response = await fetchWithRetry(`/v1/users/me`, requestOptions);
            if (cancelado) return;
            
            if (!response.ok) {
              if (response.status === 401 || response.status === 403 || response.status === 404) {
                cerrarSesion();
                setToken(null);
                return;
              }
              // Never clear session on gateway/network errors (502, 503, 504) — backend may be cold after idle
              if (response.status === 502 || response.status === 503 || response.status === 504) {
                console.warn(`[UserContext] /v1/users/me returned ${response.status}, keeping session (retry later)`);
                programarReintento();
                return;
              }
              console.warn(`[UserContext] /v1/users/me returned ${response.status}, keeping session`);
              programarReintento();
              return;
            }
            
            const data = await response.json();
            if (cancelado) return;
            
            console.log('[UserContext] User data received:', {
              email: data?.email,
              access_privileges: data?.access_privileges,
              venue_id: data?.venue_id,
              venue: data?.venue,
              owned_venues_count: data?.owned_venues?.length || 0,
              owned_venues: data?.owned_venues
            });

            setOwnedShops(data?.owned_venues || []); 
            setEmail(data?.email || "");
            setAccessPrivileges(data?.access_privileges || "");
            setCanManageProducts(!!data?.can_manage_products);

            // If superuser, fetch all venues
            if (data?.access_privileges === 'superadmin') {
              try {
                const venuesResponse = await apiFetch('/v1/venues/all', requestOptions);
                if (cancelado) return;
                if (venuesResponse.ok) {
                  const venuesData = await venuesResponse.json();
                  if (cancelado) return;
                  setAllVenues(venuesData || []);
                  console.log('[UserContext] Loaded all venues for superuser:', venuesData.length);
                  
                  // If no venue is selected, try to use the one from localStorage or first venue
                  const savedVenueID = localStorage.getItem("selectedVenueID");
                  if (savedVenueID && venuesData.find(v => String(v.id) === savedVenueID)) {
                    const savedVenue = venuesData.find(v => String(v.id) === savedVenueID);
                    persistSelectedVenue(savedVenue);
                  } else if (venuesData.length > 0) {
                    persistSelectedVenue(venuesData[0]);
                  }
                }
              } catch (err) {
                console.error('[UserContext] Error fetching all venues:', err);
              }
            } else if (data?.access_privileges === 'staff') {
              // For staff users, use venue_id from the response
              console.log('[UserContext] Processing staff user, venue data:', {
                venue_id: data?.venue_id,
                venue: data?.venue,
                hasVenue: !!data?.venue
              });
              
              if (data?.venue_id) {
                // Staff multi-tienda: si tiene seleccionada OTRA tienda suya, se
                // respeta. Antes se forzaba siempre la principal en cada montaje, así
                // que el staff no podía quedarse en la segunda tienda (bug Kong: los
                // creados en Kong House volvían a House al entrar en Kong Club).
                const assigned = Array.isArray(data?.owned_venues) ? data.owned_venues : [];
                const selectedId = localStorage.getItem("selectedVenueID");
                const selected = selectedId
                  ? assigned.find((v) => String(v.id) === String(selectedId))
                  : null;
                const staffVenueID = selected ? String(selected.id) : String(data.venue_id);
                // Datos de la tienda en uso: la seleccionada (de owned_venues, que ya
                // trae su estado de activación) o la principal (data.venue).
                const activeVenue = selected || data?.venue;
                const venueName = activeVenue?.name || "";
                const venueImage = activeVenue?.image_path || "";
                const nextVenueType = activeVenue?.venue_type || DEFAULT_VENUE_TYPE;

                // Limpiar caché solo si la tienda guardada ya NO es suya (accesos revocados).
                if (selectedId && !selected && selectedId !== staffVenueID) {
                  console.log('[UserContext] Cached venue is no longer assigned to this STAFF; resetting');
                  localStorage.removeItem("selectedVenueID");
                  localStorage.removeItem("selectedVenueName");
                  localStorage.removeItem("selectedVenueImage");
                }
                
                // Force update venue state - this will trigger re-renders in components
                setVenueID(staffVenueID);
                setVenueName(venueName);
                setVenueImage(venueImage);
                setVenueType(nextVenueType);
                setVenueActivationStatus(activeVenue?.activation_status || "pending");
                setVenueSubscriptionStatus(activeVenue?.subscription_status || "");
                setVenueTrialEndsAt(activeVenue?.trial_ends_at || "");
                setVenueOrdersEnabled(Boolean(activeVenue?.orders_enabled));
                setVenueSmackEnabled(Boolean(activeVenue?.smack_enabled));
                localStorage.setItem("selectedVenueID", staffVenueID);
                localStorage.setItem("selectedVenueName", venueName);
                localStorage.setItem("selectedVenueImage", venueImage);
                localStorage.setItem("selectedVenueType", nextVenueType);
                localStorage.setItem("selectedVenueActivationStatus", activeVenue?.activation_status || "pending");
                localStorage.setItem("selectedVenueSubscriptionStatus", activeVenue?.subscription_status || "");
                localStorage.setItem("selectedVenueTrialEndsAt", activeVenue?.trial_ends_at || "");
                localStorage.setItem("selectedVenueOrdersEnabled", String(Boolean(activeVenue?.orders_enabled)));
                localStorage.setItem("selectedVenueSmackEnabled", String(Boolean(activeVenue?.smack_enabled)));
                console.log('[UserContext] ✅ Set venue data for staff:', {
                  id: staffVenueID,
                  name: venueName,
                  image: venueImage,
                  venueID_type: typeof staffVenueID,
                  fromResponse: !!data?.venue,
                  venueIdFromResponse: data?.venue?.id,
                  fullVenueData: data?.venue,
                  venue_id_from_data: data?.venue_id
                });
                
                // Warn if venue name is missing
                if (!venueName) {
                  console.warn('[UserContext] STAFF venue name is missing from response!', {
                    venue_id: staffVenueID,
                    venue_data: data?.venue
                  });
                }
              } else {
                console.error('[UserContext] ❌ Staff user has no venue_id assigned in response!', data);
                // Para staff sin venue_id, mostrar mensaje claro pero no romper la aplicación
                setVenueID("");
                setVenueName("⚠️ Sin tienda asignada");
                setVenueImage("");
                setVenueType(DEFAULT_VENUE_TYPE);
                setVenueActivationStatus("pending");
                setVenueSubscriptionStatus("");
                setVenueTrialEndsAt("");
                setVenueOrdersEnabled(false);
                // Clear cached data if no venue_id
                localStorage.removeItem("selectedVenueID");
                localStorage.removeItem("selectedVenueName");
                localStorage.removeItem("selectedVenueImage");
                localStorage.removeItem("selectedVenueType");
                localStorage.removeItem("selectedVenueActivationStatus");
                localStorage.removeItem("selectedVenueSubscriptionStatus");
                localStorage.removeItem("selectedVenueTrialEndsAt");
                localStorage.removeItem("selectedVenueOrdersEnabled");
              }
            } else {
              // For non-superusers (owners), use owned_venues
              const ownedVenues = data?.owned_venues || [];
              
              // Try to use saved venue if it exists and is in owned_venues
              const savedVenueID = localStorage.getItem("selectedVenueID");
              if (savedVenueID && ownedVenues.find(v => String(v.id) === savedVenueID)) {
                const savedVenue = ownedVenues.find(v => String(v.id) === savedVenueID);
                console.log('[UserContext] Using saved venue for owner:', {
                  id: savedVenue.id,
                  name: savedVenue.name
                });
                persistSelectedVenue(savedVenue);
              } else if (ownedVenues.length > 0) {
                const firstOwnedVenue = ownedVenues[0];
                console.log('[UserContext] Setting first owned venue:', {
                  id: firstOwnedVenue.id,
                  name: firstOwnedVenue.name
                });
                persistSelectedVenue(firstOwnedVenue);
              } else {
                console.warn('[UserContext] No owned venues found for user');
                setVenueID("");
                setVenueName("");
                setVenueImage("");
                setVenueType(DEFAULT_VENUE_TYPE);
                setVenueActivationStatus("pending");
                setVenueSubscriptionStatus("");
                setVenueTrialEndsAt("");
                setVenueOrdersEnabled(false);
                localStorage.removeItem("selectedVenueID");
                localStorage.removeItem("selectedVenueName");
                localStorage.removeItem("selectedVenueImage");
                localStorage.removeItem("selectedVenueType");
                localStorage.removeItem("selectedVenueActivationStatus");
                localStorage.removeItem("selectedVenueSubscriptionStatus");
                localStorage.removeItem("selectedVenueTrialEndsAt");
                localStorage.removeItem("selectedVenueOrdersEnabled");
              }
            }

            // Solo guardar token si la respuesta fue exitosa
            guardarSesion({ token });
          } catch (error) {
            if (cancelado) return;
            console.error("Error fetching user data:", error);
            // Only clear token on explicit auth errors (401/403), not on network/gateway errors (502, etc.)
            const msg = (error?.message || "").toLowerCase();
            if (msg.includes("401") || msg.includes("403")) {
              cerrarSesion();
              setToken(null);
            } else {
              programarReintento();
            }
          } finally {
            // Resuelta (bien o mal) salvo que haya un reintento en marcha o el token
            // ya sea otro.
            if (!cancelado && !reintento) setSesionResueltaPara(token);
          }
        };
        const programarReintento = () => {
          if (cancelado) return;
          const espera = ESPERAS_MS[Math.min(ronda, ESPERAS_MS.length - 1)];
          ronda += 1;
          console.warn(`[UserContext] /v1/users/me sin respuesta: reintento ${ronda} en ${espera / 1000} s`);
          reintento = setTimeout(() => {
            reintento = null;
            if (!cancelado) fetchUser();
          }, espera);
        };
        fetchUser();
        return () => {
          cancelado = true;
          if (reintento) clearTimeout(reintento);
        };
      }, [token, persistSelectedVenue]);

       // ownedShops state updated
            

    // Function to change selected venue (for superusers)
    const changeVenue = useCallback((
      venueId,
      venueName,
      venueImagePath = "",
      nextVenueType = DEFAULT_VENUE_TYPE,
      nextVenueActivationStatus = "pending",
      nextVenueSubscriptionStatus = "",
      nextVenueTrialEndsAt = "",
      nextVenueOrdersEnabled = false,
      nextVenueSmackEnabled = false
    ) => {
      setVenueID(String(venueId));
      setVenueName(venueName || "");
      setVenueImage(venueImagePath || "");
      setVenueType(nextVenueType || DEFAULT_VENUE_TYPE);
      setVenueActivationStatus(nextVenueActivationStatus || "pending");
      setVenueSubscriptionStatus(nextVenueSubscriptionStatus || "");
      setVenueTrialEndsAt(nextVenueTrialEndsAt || "");
      setVenueOrdersEnabled(Boolean(nextVenueOrdersEnabled));
      setVenueSmackEnabled(Boolean(nextVenueSmackEnabled));
      localStorage.setItem("selectedVenueID", String(venueId));
      localStorage.setItem("selectedVenueName", venueName || "");
      localStorage.setItem("selectedVenueImage", venueImagePath || "");
      localStorage.setItem("selectedVenueType", nextVenueType || DEFAULT_VENUE_TYPE);
      localStorage.setItem("selectedVenueActivationStatus", nextVenueActivationStatus || "pending");
      localStorage.setItem("selectedVenueSubscriptionStatus", nextVenueSubscriptionStatus || "");
      localStorage.setItem("selectedVenueTrialEndsAt", nextVenueTrialEndsAt || "");
      localStorage.setItem("selectedVenueOrdersEnabled", String(Boolean(nextVenueOrdersEnabled)));
      localStorage.setItem("selectedVenueSmackEnabled", String(Boolean(nextVenueSmackEnabled)));
      console.log('[UserContext] Venue changed to:', {
        id: venueId,
        name: venueName,
        image: venueImagePath,
        venueType: nextVenueType,
        activationStatus: nextVenueActivationStatus,
        subscriptionStatus: nextVenueSubscriptionStatus,
      });
    }, []);

    // Function to refresh venues data
    const refreshVenues = useCallback(async () => {
      if (!token) return;
      
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      };

      try {
        // Refresh user data to get updated owned_venues
        const response = await fetch(`/v1/users/me`, requestOptions);
        if (!response.ok) return;
        
        const data = await response.json();
        setOwnedShops(data?.owned_venues || []);
        
        // If superuser, refresh all venues
        if (data?.access_privileges === 'superadmin') {
          const venuesResponse = await fetch('/v1/venues/all', requestOptions);
          if (venuesResponse.ok) {
            const venuesData = await venuesResponse.json();
            setAllVenues(venuesData || []);
            
            // Update selected venue if it exists in the refreshed list
            const currentVenueID = venueID;
            if (currentVenueID) {
              const updatedVenue = venuesData.find(v => String(v.id) === currentVenueID);
              if (updatedVenue) {
                persistSelectedVenue(updatedVenue);
              }
            }
          }
        } else {
          // For owners, update selected venue from owned_venues
          const ownedVenues = data?.owned_venues || [];
          const currentVenueID = venueID;
          if (currentVenueID) {
            const updatedVenue = ownedVenues.find(v => String(v.id) === currentVenueID);
            if (updatedVenue) {
              persistSelectedVenue(updatedVenue);
            }
          }
        }
      } catch (error) {
        console.error('[UserContext] Error refreshing venues:', error);
      }
    }, [token, venueID, persistSelectedVenue]);

    return(
        <UserContext.Provider value={[
          token, 
          setToken, 
          ownedShops, 
          venueID, 
          venueName, 
          venueImage,
          email, 
          accessPrivileges,
          allVenues,
          changeVenue,
          refreshVenues,
          venueType,
          venueActivationStatus,
          venueSubscriptionStatus,
          canManageProducts,
          venueTrialEndsAt,
          // Nuevos valores van SIEMPRE al final: el contexto es un array y los
          // consumidores lo destructuran por posición.
          venueOrdersEnabled,
          // Actualiza el flag del comandero SIN recargar. Configuración lo llama
          // tras guardar: antes solo se mandaba al servidor, y como el menú se
          // pinta con el valor cacheado, «Barra» no aparecía hasta la SEGUNDA
          // recarga (reporte del usuario, 18-ago).
          aplicarOrdersEnabled,
          // Smack (posiciones 18 y 19): mismo par flag+actualizador que el comandero.
          venueSmackEnabled,
          aplicarSmackEnabled,
          // Posición 20: true cuando /v1/users/me ya resolvió para el token actual.
          // Fuente única de "sesión resuelta" — ver useUserTokenPrivileges.
          userReady
        ]}>
            {props.children}
        </UserContext.Provider>
    )

};
